"use client"
import React, { useState, useEffect } from "react";
import Asset from "@/components/shared/Asset";
import MetadataThumbnail from "@/components/shared/MetadataThumbnail";
import CommunityDirectoryMemberView from "@/components/communities/CommunityDirectoryMemberView";
import MapLink from "@/components/shared/MapLink";

import "@/components/maps/MapPointContent.css";
import MediaSubmissionModal from "@/components/media/MediaSubmissionModal";
import CommentField from "@/components/shared/CommentField";
import EventParticipantGroupParticipantView from "@/components/eventmanagement/EventParticipantGroupParticipantView";
import { OverlayTrigger, Popover } from "react-bootstrap";
import ContentCard from "@/components/shared/ContentCard";

import { useMediaQuery } from "react-responsive";
import EventCalendarEntry from "@/components/events/EventCalendarEntry";

var moment = require("moment");

const MapPointContent = ({
  name,
  point,
  numbered,
  showTitle,
  mapId,
  ownerId,
  external,
  onClick,
  supportsMediaSubmissions,
  onMediaSubmit,
  showCaption,
  enableComments,
  locationTag
}) => {
  const isSm = useMediaQuery({ maxWidth: 767.98 });
  const [metadata, setMetadata] = useState(null);
  const [uralityProfile, setUralityProfile] = useState(null);
  const [prettyPrintedFields, setPrettyPrintedFields] = useState(null);
  const [showMediaSubmission, setShowMediaSubmission] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // console.log("MapPointContent useEffect point", point);
    if (point) {
      if (point.metadata && point.metadata !== "") {
        var myMetadata = JSON.parse(point.metadata);
        setMetadata(myMetadata);
      }
      if (
        point.location &&
        point.location.locationSource === "URALITY_PROFILE"
      ) {
        var profile = point.location.userProfile;
        if (profile) {
          profile["location"] = point.location;
          setUralityProfile(profile);
        } else {
          setUralityProfile(null);
        }
      } else {
        setUralityProfile(null);
      }
      if (point.readOnlyFields) {
        setPrettyPrintedFields(prettyPrintFields(point.readOnlyFields));
      }
      setLoaded(true);
    }
  }, [point]);

  const prettyPrintFields = fields => {
    var text = "";
    for (var key in fields) {
      if (fields[key]) {
        text += `${key}: ${fields[key]}\n`;
      }
    }
    return text;
  };

  const submissionOwnerLabel = up => {
    var label = "Anonymous user";
    if (up.givenName && up.familyName) {
      label = `${up.givenName} ${up.familyName}`;
    } else if (up.displayName) {
      label = up.displayName;
    }
    return label;
  };

  if (loaded) {
    if (point.type === "EVENT_PARTICIPANT") {
      return (
        <EventParticipantGroupParticipantView
          name={name}
          point={point}
          locationTag={locationTag}
          onClick={onClick}
        />
      );
    } else if (point.type === "EVENT_SCHEDULE") {
      return (
        <EventCalendarEntry
          name={name}
          wrapperClassName={"clear"}
          external={external}
          event={point}
          small={true}
          showMap={true}
          onClick={onClick}
        />
      );
    } else {
      return (
        <>
          {showMediaSubmission && (
            <MediaSubmissionModal
              multiple={true}
              onSubmit={(payload, callback, token) => {
                onMediaSubmit(payload, token, success => {
                  if (success) {
                    setShowMediaSubmission(false);
                    callback(success);
                  }
                });
              }}
              onHide={() => setShowMediaSubmission(false)}
            />
          )}
          <div
            className="map-point-content"
            id={`map-point-content-${point ? point.id : "null"}`}
          >
            {(point.source == null || point.source === "URALITY") && (
              <div className="tag">
                {point.external && point.userProfile && (
                  <div className="content-submission">
                    <span id={`submission-owner-${point.userProfile.id}`}>
                      Submitted by&nbsp;
                      <>
                        <OverlayTrigger
                          trigger={isSm ? "click" : ["hover", "focus"]}
                          id={`submission-owner-${point.userProfile.id}`}
                          placement="bottom"
                          overlay={
                            <Popover>
                              <Popover.Body style={{ padding: 0, margin: 0 }}>
                                <ContentCard
                                  className="content-card-in-popover"
                                  name={name}
                                  id={null}
                                  community={null}
                                  waypoint={null}
                                  type={"USER_PROFILE"}
                                  content={point.userProfile}
                                  external={false}
                                  clickable={false}
                                />
                              </Popover.Body>
                            </Popover>
                          }
                        >
                          <span>{submissionOwnerLabel(point.userProfile)}</span>
                        </OverlayTrigger>
                      </>
                    </span>
                  </div>
                )}
                <div className="asset-and-content" onClick={onClick}>
                  <div
                    className={`asset-and-content-header ${
                      supportsMediaSubmissions
                        ? "supports-media-submissions"
                        : ""
                    } ${
                      point.assets && point.assets.length > 1
                        ? "multiple-assets"
                        : point.assets && point.assets.length === 0
                        ? "no-assets"
                        : ""
                    }`}
                  >
                    {supportsMediaSubmissions && (
                      <>
                        <div className="submission-header">
                          <button
                            type="button"
                            className="a with-icon"
                            onClick={evt => {
                              evt.preventDefault();
                              evt.stopPropagation();
                              setShowMediaSubmission(true);
                            }}
                          >
                            <span className="material-icons">add</span>
                            Contribute Media
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="map-point-content-asset">
                    {point.assets && point.assets.length > 0 && (
                      <Asset assets={point.assets} showCaption={true} />
                    )}
                  </div>
                  <div className="default-card-wrapper-content">
                    {point.title && showTitle && (
                      <h3>
                        {numbered && `${point.index}. `}
                        {point.title}
                      </h3>
                    )}
                    {point.text && <p>{point.text}</p>}
                    {prettyPrintedFields && <p>{prettyPrintedFields}</p>}
                  </div>
                </div>
                {point.location && point.displayLocation && (
                  <MapLink
                    className="map-point-content-map-link"
                    location={point.location}
                    clickable={true}
                  />
                )}
                {point.url && point.url !== "" && metadata && (
                  <MetadataThumbnail
                    ownerId={ownerId}
                    contentId={mapId}
                    type="STORY"
                    image={true}
                    metadata={metadata}
                    url={point.url}
                    clickable={true}
                    className="map-point-external-link"
                  />
                )}
                {uralityProfile && (
                  <CommunityDirectoryMemberView
                    size="sm"
                    className="map-point-profile-view"
                    profile={uralityProfile}
                    external={external}
                    sendToExternalWebsite={true}
                  />
                )}
                {enableComments && (
                  <div className="comment-field default-card-wrapper-content">
                    <CommentField
                      type="STORY_MAP"
                      contentId={mapId}
                      subContentId={point.id}
                    />
                  </div>
                )}
                {point.comments && point.comments.length > 0 && (
                  <div className="default-card-wrapper-content comment-container">
                    <h4>Public Comments</h4>
                    {point.comments.map((comment, index) => {
                      return (
                        <div className="comment">
                          <div className="div with-icon">
                            <span className="material-icons">comment</span>
                            <span>
                              {moment(comment.createdDate).format(
                                "MMMM Do YYYY, h:mm a"
                              )}
                            </span>
                          </div>
                          <span style={{ marginLeft: "1.75rem" }}>
                            {comment.content}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      );
    }
  } else {
    return <></>;
  }
};
export default MapPointContent;
